import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { toStruckRuns } from 'helpers/richTextDiff';

const STRIKE = 'tw-line-through';

// Class for one richWordDiff part. Words struck through in the PIF itself stay
// struck; a word struck through *in this version* (new side, changed + struck)
// reads as a removal-in-place rather than an addition.
const partClass = (part, tone, dimUnchanged) => {
  if (part.changed && tone === 'old') return `tw-text-rose-600 ${STRIKE} tw-bg-rose-50 tw-rounded-sm`;
  if (part.changed && part.struck) return `tw-text-rose-600 ${STRIKE} tw-font-semibold tw-bg-amber-100 tw-rounded-sm`;
  if (part.changed) return 'tw-text-emerald-700 tw-font-semibold tw-bg-emerald-50 tw-rounded-sm';

  const base = dimUnchanged ? (tone === 'old' ? 'tw-text-slate-400' : 'tw-text-slate-500') : '';
  return part.struck ? `${base} ${STRIKE}`.trim() : base;
};

const partTitle = (part, tone) => {
  if (!part.changed) return undefined;
  if (tone === 'new' && part.struck) return 'Struck through in this version';
  if (tone === 'old' && part.struck) return 'Strike-through removed in this version';
  return undefined;
};

// Renders the { text, changed, struck } parts of helpers/richTextDiff.richWordDiff
// (one side), putting a plain space between parts so highlights hug the words.
const DiffWords = ({ parts, tone, dimUnchanged = true, className = 'tw-break-words' }) => (
  <span className={className}>
    {parts.map((part, i) => (
      <Fragment key={i}>
        {i > 0 && ' '}
        <span className={partClass(part, tone, dimUnchanged) || undefined} title={partTitle(part, tone)}>
          {part.text}
        </span>
      </Fragment>
    ))}
  </span>
);

DiffWords.propTypes = {
  parts: PropTypes.arrayOf(PropTypes.shape({
    text: PropTypes.string,
    changed: PropTypes.bool,
    struck: PropTypes.bool,
  })).isRequired,
  tone: PropTypes.oneOf(['old', 'new']).isRequired,
  dimUnchanged: PropTypes.bool,
  className: PropTypes.string,
};

// Keeps the runs covering the first `max` characters of the space-joined text.
const truncateRuns = (runs, max) => runs.reduce(
  (acc, run) => {
    const room = max - acc.pos;
    const piece = room > 0 ? [{ ...run, text: run.text.slice(0, room) }] : [];
    return { pos: acc.pos + run.text.length + 1, out: [...acc.out, ...piece] };
  },
  { pos: 0, out: [] }
).out;

// A whole rich-text value as plain text, keeping words struck through in the PIF
// struck. `max` caps the characters shown (trailing "…").
export const StruckText = ({ value, className, max }) => {
  const runs = toStruckRuns(value);
  const total = runs.reduce((n, r, i) => n + r.text.length + (i > 0 ? 1 : 0), 0);
  const cut = max != null && total > max;
  const shown = cut ? truncateRuns(runs, max) : runs;

  return (
    <span className={className}>
      {shown.map((run, i) => (
        <Fragment key={i}>
          {i > 0 && ' '}
          <span className={run.struck ? STRIKE : undefined}>{run.text}</span>
        </Fragment>
      ))}
      {cut ? '…' : ''}
    </span>
  );
};

StruckText.propTypes = {
  value: PropTypes.any,
  className: PropTypes.string,
  max: PropTypes.number,
};

export default DiffWords;
